import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO, { blogPostingSchema } from '../components/SEO';
import {
  doc, getDoc, collection, getDocs,
  query, where, orderBy, limit,
  updateDoc, increment, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import BlogNavbar from './BlogNavbar';
import BlogFooter from './BlogFooter';
import '../Styles/Blog.css';
import { BotanicalStem, BotanicalLeafOutline } from './BotanicalSVG';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import VoiceReader from '../components/VoiceReader';
import ReadingMomentum from '../components/ReadingMomentum';

/* ── UTILS ── */

const parseContent = (html = '') => {
  const text  = html.replace(/<[^>]+>/g, ' ').trim();
  const words = text.split(/\s+/).filter(Boolean);
  return { wordCount: words.length, readMins: Math.max(1, Math.ceil(words.length / 200)) };
};

const extractHeadings = (html = '') => {
  const re  = /<h([23])[^>]*>([\s\S]*?)<\/h\1>/gi;
  const out = [];
  let m, i  = 0;
  while ((m = re.exec(html)) !== null) {
    out.push({ id: `bh-${i++}`, level: `h${m[1]}`, text: m[2].replace(/<[^>]+>/g, '').trim() });
  }
  return out;
};

const patchHeadingIds = (html = '') => {
  let i = 0;
  return html.replace(/<(h[23])([^>]*?)>/gi, (_, tag, attrs) => {
    const clean = attrs.replace(/\s*id="[^"]*"/gi, '');
    return `<${tag}${clean} id="bh-${i++}">`;
  });
};

const formatDate = val => {
  if (!val) return null;
  const d = val?.toDate ? val.toDate() : new Date(val);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const fmtCommentDate = val => {
  if (!val) return '';
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ── READING PROGRESS BAR ── */
const ReadingProgress = () => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el    = document.documentElement;
      const total = Math.max(0, el.scrollHeight - window.innerHeight);
      setPct(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return (
    <div className="blog-progress-track" aria-hidden="true">
      <div className="blog-progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
};

/* ── TABLE OF CONTENTS ── */
const BlogTOC = ({ headings, activeId }) => (
  <aside className="blog-toc" aria-label="Table of contents">
    <p className="blog-toc-label">In this article</p>
    <nav>
      {headings.map(h => (
        <a
          key={h.id}
          href={`#${h.id}`}
          className={`blog-toc-item ${h.level}${activeId === h.id ? ' active' : ''}`}
          onClick={e => {
            e.preventDefault();
            document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          {h.text}
        </a>
      ))}
    </nav>
  </aside>
);

/* ── LIKE BUTTON ── */
const LikeButton = ({ slug, initialCount }) => {
  const likedKey = `pp_liked_${slug}`;
  const [liked, setLiked]   = useState(() => !!localStorage.getItem(likedKey));
  const [count, setCount]   = useState(initialCount || 0);
  const [bounce, setBounce] = useState(false);

  const handleLike = () => {
    if (liked) return;
    setLiked(true);
    setCount(c => c + 1);
    setBounce(true);
    setTimeout(() => setBounce(false), 600);
    localStorage.setItem(likedKey, '1');
    updateDoc(doc(db, 'blogs', slug), { likes: increment(1) }).catch(() => {});
  };

  return (
    <button
      className={`blog-like-btn${liked ? ' liked' : ''}${bounce ? ' bounce' : ''}`}
      onClick={handleLike}
      aria-label={liked ? 'Liked' : 'Like this article'}
      title={liked ? 'You liked this!' : 'Like this article'}
    >
      <span className="blog-like-heart">{liked ? '♥' : '♡'}</span>
      <span className="blog-like-count">{count}</span>
      <span className="blog-like-label">{liked ? 'Liked!' : 'Like'}</span>
    </button>
  );
};

/* ── COMMENTS SECTION ── */
const CommentsSection = ({ slug }) => {
  const [comments, setComments]       = useState([]);
  const [loadingC, setLoadingC]       = useState(true);
  const [form, setForm]               = useState({ name: '', text: '' });
  const [honeypot, setHoneypot]       = useState('');
  const [status, setStatus]           = useState('idle'); // idle | sending | success | error
  const loadTimeRef                   = useRef(Date.now());

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'blogs', slug, 'comments'), orderBy('createdAt', 'asc'))
        );
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setComments(all.filter(c => c.approved === true));
      } catch {
        setComments([]);
      } finally {
        setLoadingC(false);
      }
    };
    load();
  }, [slug]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (honeypot) return;
    if (Date.now() - loadTimeRef.current < 3000) return;
    const { name, text } = form;
    if (!name.trim() || !text.trim()) return;
    setStatus('sending');
    try {
      await addDoc(collection(db, 'blogs', slug, 'comments'), {
        name: name.trim(),
        text: text.trim(),
        approved: false,
        createdAt: serverTimestamp(),
      });
      setStatus('success');
      setForm({ name: '', text: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="blog-comments">
      <h3 className="blog-comments-heading">
        💬 {comments.length > 0 ? `${comments.length} Comment${comments.length !== 1 ? 's' : ''}` : 'Leave a Comment'}
      </h3>

      {/* Existing comments */}
      {!loadingC && comments.length > 0 && (
        <div className="blog-comments-list">
          {comments.map(c => (
            <div key={c.id} className="blog-comment">
              <div className="blog-comment-avatar">{(c.name || '?')[0].toUpperCase()}</div>
              <div className="blog-comment-body">
                <div className="blog-comment-meta">
                  <strong className="blog-comment-name">{c.name}</strong>
                  <time className="blog-comment-date">{fmtCommentDate(c.createdAt)}</time>
                </div>
                <p className="blog-comment-text">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comment form */}
      <div className="blog-comment-form-wrap">
        <h4 className="blog-comment-form-title">Share your thoughts</h4>
        <p className="blog-comment-form-note">Comments are reviewed before appearing.</p>

        {status === 'success' ? (
          <div className="blog-comment-success">
            <span>✓</span> Thanks! Your comment is under review and will appear soon.
          </div>
        ) : (
          <form className="blog-comment-form" onSubmit={handleSubmit}>
            {/* Honeypot */}
            <input
              type="text"
              value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, width: 1, height: 1 }}
              autoComplete="off"
            />
            <div className="blog-comment-fields">
              <input
                type="text"
                placeholder="Your name *"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="blog-comment-input"
                required
                maxLength={60}
              />
              <textarea
                placeholder="Write your comment…"
                value={form.text}
                onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                className="blog-comment-textarea"
                required
                rows={4}
                maxLength={1000}
              />
            </div>
            {status === 'error' && (
              <p style={{ color: '#c25b2a', fontSize: '0.85rem', margin: '0.4rem 0' }}>
                Something went wrong. Please try again.
              </p>
            )}
            <button type="submit" className="blog-comment-submit" disabled={status === 'sending'}>
              {status === 'sending' ? 'Submitting…' : 'Post Comment'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ── MAIN COMPONENT ── */
const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost]                   = useState(null);
  const [related, setRelated]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [copied, setCopied]               = useState(false);
  const [activeHeading, setActiveHeading] = useState(null);
  const [lightboxSrc, setLightboxSrc]     = useState(null);
  const contentRef = useRef(null);

  /* ── FETCH + VIEW TRACKING ── */
  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchPost = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'blogs', slug));
        if (!docSnap.exists()) { setLoading(false); return; }

        const data = { id: docSnap.id, ...docSnap.data() };
        setPost(data);

        /* View tracking — increment once per 24h per browser */
        const viewKey = `pp_viewed_${slug}`;
        const lastViewed = parseInt(localStorage.getItem(viewKey) || '0');
        if (Date.now() - lastViewed > 24 * 60 * 60 * 1000) {
          updateDoc(doc(db, 'blogs', slug), { views: increment(1) }).catch(() => {});
          localStorage.setItem(viewKey, String(Date.now()));
        }

        /* Related posts */
        let rel = [];
        const firstTag = data.tags?.[0];
        if (firstTag) {
          try {
            const tagSnap = await getDocs(
              query(collection(db, 'blogs'), where('tags', 'array-contains', firstTag), limit(5))
            );
            rel = tagSnap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(d => d.id !== docSnap.id);
          } catch (_) {}
        }
        if (rel.length < 3) {
          const anySnap = await getDocs(
            query(collection(db, 'blogs'), orderBy('createdAt', 'desc'), limit(6))
          );
          const pad = anySnap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .filter(d => d.id !== docSnap.id && !rel.find(r => r.id === d.id));
          rel = [...rel, ...pad];
        }
        setRelated(rel.slice(0, 3));
      } catch (err) {
        console.error('BlogDetail fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  /* ── DERIVED STATE ── */
  const { wordCount, readMins } = useMemo(
    () => post ? parseContent(post.content) : { wordCount: 0, readMins: 0 },
    [post]
  );
  const patchedContent = useMemo(() => sanitizeHtml(patchHeadingIds(post?.content || '')), [post]);
  const headings       = useMemo(() => extractHeadings(post?.content || ''), [post]);
  const formattedDate  = useMemo(() => formatDate(post?.createdAt || post?.publishedAt), [post]);
  const hasToc         = headings.length >= 3;

  /* ── ARTICLE MEDIA: image zoom + iframe resize ── */
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !patchedContent) return;

    // Image lightbox on click (event delegation)
    const onImgClick = e => {
      if (e.target.tagName === 'IMG') setLightboxSrc(e.target.src);
    };
    el.addEventListener('click', onImgClick);

    // Wrap iframes in resizable container
    el.querySelectorAll('iframe').forEach(iframe => {
      if (iframe.parentElement?.classList.contains('article-iframe-wrap')) return;
      const wrap = document.createElement('div');
      wrap.className = 'article-iframe-wrap';
      iframe.parentNode.insertBefore(wrap, iframe);
      wrap.appendChild(iframe);
    });

    return () => el.removeEventListener('click', onImgClick);
  }, [patchedContent]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── ACTIVE HEADING OBSERVER ── */
  useEffect(() => {
    if (!hasToc) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveHeading(e.target.id); }),
      { rootMargin: '-15% 0% -75% 0%', threshold: 0 }
    );
    headings.forEach(h => {
      const el = document.getElementById(h.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [headings, hasToc]);

  /* ── COPY LINK ── */
  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }).catch(() => {});
  }, []);

  /* ── LOADING ── */
  if (loading) {
    return (
      <div className="blog-container">
        <BlogNavbar />
        <div className="blog-detail-skeleton">
          <div className="blog-skel-hero" />
          <div className="blog-skel-body">
            {[80, 60, 90, 50, 75, 40].map((w, i) => (
              <div key={i} className="blog-skel-line" style={{ width: `${w}%` }} />
            ))}
          </div>
        </div>
        <BlogFooter />
      </div>
    );
  }

  /* ── NOT FOUND ── */
  if (!post) {
    return (
      <div className="blog-container">
        <BlogNavbar />
        <div className="blog-error">
          <h2>Article Not Found</h2>
          <p>We couldn't find that article.</p>
          <Link to="/blog" className="back-link">← Back to Blog</Link>
        </div>
        <BlogFooter />
      </div>
    );
  }

  /* ── OG / SEO ── */
  const postSlug    = post.slug || post.id;
  const ogImage     = post.thumbnail || '';
  const ogTitle     = post.title || 'The PP Edit';
  const ogDesc      = post.excerpt || 'Insights on contracts, freelance relationships, and professional growth.';
  const pubDate     = post.createdAt?.toDate
    ? post.createdAt.toDate().toISOString()
    : (post.publishedAt?.toDate ? post.publishedAt.toDate().toISOString() : '');
  const modDate     = post.updatedAt?.toDate ? post.updatedAt.toDate().toISOString() : pubDate;
  const postKeywords = [
    ...(post.tags || []),
    'contract specialist London', 'The PP Edit', 'freelance legal advice',
  ].join(', ');

  return (
    <div className="blog-container">
      <SEO
        title={`${ogTitle} — The PP Edit`}
        description={ogDesc}
        keywords={postKeywords}
        canonical={`/blog/${postSlug}`}
        ogImage={ogImage || undefined}
        ogType="article"
        schema={blogPostingSchema({
          title: ogTitle,
          description: ogDesc,
          slug: postSlug,
          image: ogImage,
          datePublished: pubDate,
          dateModified: modDate,
          keywords: postKeywords,
        })}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "The PP Edit", path: "/blog" },
          { name: ogTitle, path: `/blog/${postSlug}` },
        ]}
      />

      <ReadingProgress />
      <BlogNavbar />

      {/* ── FULL-BLEED HERO ── */}
      <div
        className={`blog-cover-hero${!post.thumbnail ? ' blog-cover-hero--no-img' : ''}`}
        style={post.thumbnail ? {
          backgroundImage: `url(${post.thumbnail})`,
          backgroundPosition: `${post.coverPositionX ?? 50}% ${post.coverPositionY ?? 50}%`,
        } : {}}
      >
        <div className="blog-cover-overlay" />
        <Link to="/blog" className="blog-cover-back">← All Articles</Link>
        <div className="blog-cover-inner">
          <div className="blog-cover-meta">
            {formattedDate && <time dateTime={formattedDate}>{formattedDate}</time>}
            {post.views != null && <><span>•</span><span>{post.views.toLocaleString()} views</span></>}
            <span>•</span><span>{readMins} min read</span>
            {wordCount > 0 && <><span>•</span><span>{wordCount.toLocaleString()} words</span></>}
            {post.likes > 0 && <><span>•</span><span>♥ {post.likes}</span></>}
          </div>
          <h1 className="blog-cover-title">{post.title}</h1>
          {post.excerpt && <p className="blog-cover-excerpt">{post.excerpt}</p>}
          {post.tags?.length > 0 && (
            <div className="blog-cover-tags">
              {post.tags.map(tag => <span key={tag} className="blog-cover-tag">#{tag}</span>)}
            </div>
          )}
        </div>
      </div>

      <article className="blog-detail" style={{ position: 'relative' }}>
        <BotanicalStem
          style={{ position: 'absolute', top: 80, left: -80, opacity: 0.18, zIndex: 0 }}
        />
        <BotanicalLeafOutline
          style={{ position: 'absolute', top: 320, right: -70, opacity: 0.15, zIndex: 0 }}
        />

        {/* Voice reader */}
        <VoiceReader title={post.title} content={post.content} contentRef={contentRef} audioUrl={post.audioUrl} audioWords={post.audioWords} />

        {/* Content + optional TOC sidebar */}
        <div className={`blog-detail-layout${hasToc ? ' has-toc' : ''}`}>
          <div
            ref={contentRef}
            className="blog-detail-content"
            dangerouslySetInnerHTML={{ __html: patchedContent }}
          />
          {hasToc && <BlogTOC headings={headings} activeId={activeHeading} />}
        </div>

        {/* Like + Share row */}
        <div className="blog-engage-row">
          <LikeButton slug={post.id || post.slug} initialCount={post.likes || 0} />
          <button
            className={`blog-share-btn${copied ? ' copied' : ''}`}
            onClick={handleShare}
            aria-label="Copy link"
          >
            {copied ? '✓ Copied!' : '🔗 Share'}
          </button>
        </div>

        {/* Author card */}
        {post.author ? (
          <div className="blog-author-card">
            {post.author.avatar && (
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="blog-author-img"
                onError={e => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="blog-author-info">
              <span className="blog-author-label">Written by</span>
              <h3 className="blog-author-name">{post.author.name}</h3>
              {post.author.role && <p className="blog-author-role">{post.author.role}</p>}
              {post.author.bio  && <p className="blog-author-bio">{post.author.bio}</p>}
            </div>
          </div>
        ) : (
          <footer className="blog-detail-footer">
            <div className="blog-detail-author">
              <div className="author-info">
                <h3>PP Studio</h3>
                <p>Contract specialist, freelance advocate, and professional relationship builder.</p>
              </div>
            </div>
          </footer>
        )}

        {/* Comments */}
        <CommentsSection slug={post.id || post.slug} />

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Link to="/blog" className="back-link-bottom">← Back to All Articles</Link>
        </div>
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="blog-related">
          <div className="blog-content-wrapper">
            <h2 className="section-title">More Articles</h2>
            <div className="blog-related-grid">
              {related.map(r => (
                <Link key={r.id} to={`/blog/${r.slug || r.id}`} className="blog-related-card">
                  {r.thumbnail && (
                    <div
                      className="blog-related-img"
                      style={{ backgroundImage: `url(${r.thumbnail})` }}
                    />
                  )}
                  <div className={`blog-related-body${!r.thumbnail ? ' no-img' : ''}`}>
                    {r.tags?.[0] && <span className="tag">#{r.tags[0]}</span>}
                    <h4 className="blog-related-title">{r.title}</h4>
                    {r.excerpt && <p className="blog-related-excerpt">{r.excerpt}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <BlogFooter />

      {lightboxSrc && (
        <div className="article-lightbox" onClick={() => setLightboxSrc(null)}>
          <img src={lightboxSrc} alt="Enlarged view" onClick={e => e.stopPropagation()} />
          <button className="article-lightbox-close" onClick={() => setLightboxSrc(null)}>✕</button>
        </div>
      )}

      <ReadingMomentum />
    </div>
  );
};

export default BlogDetail;
